import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

const leadSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  role: z.string().trim().max(80).optional().default(""),
  workEmail: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().default(""),
  organizationName: z.string().trim().min(2).max(160),
  orgNumber: z.string().trim().max(20).optional().default(""),
  unitCount: z.string().trim().max(20).optional().default(""),
  primaryNeed: z.string().trim().max(120).optional().default(""),
  desiredTime: z.string().trim().max(120).optional().default(""),
  message: z.string().trim().max(2000).optional().default(""),
  ctaOrigin: z.string().trim().max(80).optional().default(""),
  /** Serverutfärdad, signerad formulärbiljett. */
  formToken: z.string().trim().max(300).optional().default(""),
  /** Honeypot: fylls bara i av bottar och måste vara tomt. */
  companyWebsite: z.string().max(200).optional().default(""),
  consentPrivacy: z.literal(true),
});

function serverClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const submitForSkolorLead = createServerFn({ method: "POST" })
  .validator((data: unknown) => leadSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    const { verifyFormGuard } = await import("@/lib/form-token.server");
    await verifyFormGuard({ scope: "lead", token: data.formToken, honeypot: data.companyWebsite, school: null });

    const { enforceRateLimit, rateLimitKey } = await import("@/lib/rate-limit.server");
    await enforceRateLimit(
      "lead:ip",
      rateLimitKey(data.formToken),
      [
        { windowMinutes: 60, max: 3 },
        { windowMinutes: 1440, max: 10 },
      ],
      "Du har skickat flera förfrågningar nyligen, så vi pausar tillfälligt.",
    );

    // anon får inte längre skriva direkt mot tabellen — skrivningen sker med
    // servernyckeln efter validering, rate limiting och honeypot-kontroll.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("for_skolor_leads")
      .insert({
        full_name: data.fullName,
        role: data.role || null,
        work_email: data.workEmail,
        phone: data.phone || null,
        organization_name: data.organizationName,
        org_number: data.orgNumber || null,
        unit_count: data.unitCount || null,
        primary_need: data.primaryNeed || null,
        desired_time: data.desiredTime || null,
        message: data.message || null,
        source: "for-skolor-page",
        cta_origin: data.ctaOrigin || null,
        consent_privacy: data.consentPrivacy,
      })
      .select("id, created_at")
      .single();
    if (error) throw new Error(error.message);

    // Ingen förfrågan får ligga osedd: skolan får en kvittens och vi får en
    // notis. Nivån aktiveras fortfarande för hand.
    const plan = (data.primaryNeed || "").trim();
    const betald = plan === "Growth" || plan === "Huvudman";
    const planLabel = betald ? `Skolvyn ${plan}` : "Skolvyn";
    const { sendReceiptEmail } = await import("@/lib/email-templates/send-email");
    const { REPORT_NOTICE_RECIPIENTS } = await import("@/lib/email-templates/registry");

    if (betald) {
      await sendReceiptEmail("plan-request-receipt", data.workEmail, {
        templateData: {
          contactName: data.fullName,
          organizationName: data.organizationName,
          planLabel,
          message: data.message || null,
        },
        idempotencyKey: `plan-request-receipt-${row!.id}`,
      });
    }

    for (const mottagare of REPORT_NOTICE_RECIPIENTS) {
      await sendReceiptEmail(
        betald ? "plan-request-internal" : "lead-internal-notice",
        mottagare,
        {
          templateData: betald
            ? {
                planLabel,
                organizationName: data.organizationName,
                contactName: data.fullName,
                contactEmail: data.workEmail,
                message: data.message || null,
                ctaOrigin: data.ctaOrigin || null,
              }
            : {
                leadType: "Nytt lead från För skolor",
                schoolName: data.organizationName,
                contactName: data.fullName,
                contactEmail: data.workEmail,
                contactPhone: data.phone || null,
                role: data.role || null,
                question: data.message || null,
              },
          idempotencyKey: `lead-notis-${row!.id}-${mottagare}`,
        },
      );
    }

    return { id: row!.id as string, createdAt: row!.created_at as string, planLabel, betald };
  });
